import React from 'react';

import ChangeMarketForm from './ChangeMarketForm/ChangeMarketForm';
import * as t from 'io-ts';

import Selection from 'src/components/Selection/Selection';
// eslint-disable-next-line no-restricted-imports
import { observer } from 'mobx-react';
import { AppStateComponent } from 'src/AppState/AppState';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { getMarketList } from '../MarketsList/getMarketList';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { createGuard } from 'src_common/common/createGuard';
import { TypeId } from 'src_common/common/websocket2/type';
import { isCriteria, sortSelectionsByCriteria } from 'src_common/utils/sport/sort';

const SelectedMarketIO = t.interface({
    market: t.string,
});

const isSelectedMarket = createGuard(SelectedMarketIO);

const SELECTION_ORDER_default = [ 'A', 'D', 'H' ];

const SELECTION_ORDER: Record<string, Array<string>> = {
    'race-to': [ 'A', 'N', 'H' ],
    'both-teams-to-score': [ 'N', 'Y' ],
    'away-to-win-to-nil': [ 'N', 'Y' ],
    'home-to-win-to-nil': [ 'N', 'Y' ],
    'home-clean-sheet': [ 'N', 'Y' ],
    'away-clean-sheet': [ 'N', 'Y' ],
    'red-card-in-match': [ 'N', 'Y' ],
    'overtime': [ 'N', 'Y' ],
    'will-runs-be-scored-by-first-ball': [ 'N', 'Y' ],
    'first-over-runs-odd-even': [ 'E', 'O' ],
    'double-chance': [ 'H-A', 'A-D', 'H-D' ],
    'time-of-1st-goal-over-under': [ '0-30', 'NG', '31-90' ],
    'time-of-2nd-goal-over-under': [ '0-50', 'NG', '51-90' ],
    'time-of-3rd-goal-over-under': [ '0-70', 'NG', '71-90' ],
    'fight-end': [ 'D', 'KO', 'PNTS' ],
    'total-frames': [ 'U', 'Ext', 'O' ],
    // default: [ 'A', 'D', 'H' ]
    default: SELECTION_ORDER_default,
};

const getList = (out: Map<string, SelectionModel[]>, key: string): Array<SelectionModel> => {
    const item = out.get(key);
    if (item !== undefined) {
        return item;
    }

    const newList: Array<SelectionModel> = [];
    out.set(key, newList);
    return newList;
};

const getGroupList = (items: Array<SelectionModel>, displayTemplate: 'one-column' | 'goalscorer' | 'two-column' | 'three-column'): Map<string, SelectionModel[]> => {
    const groups: Array<SelectionModel[]> = [];

    if (displayTemplate === 'one-column' || displayTemplate === 'goalscorer') {
        groups[0] = items;
    } else {
        const limitRowToDisplay = {
            'two-column': items.length / 2,
            'three-column': items.length / 3
        };

        const limit = Math.round(limitRowToDisplay[displayTemplate]);

        const n = Math.round(items.length / limit);

        for ( let i = 0; i < n; i++) {
            const start = i * limit;
            const finish = i === n - 1 ? items.length : start + limit;
            groups[i] = items.slice(start, finish);
        }
    }

    const accumulator: Map<string, SelectionModel[]> = new Map();

    for (const obj of groups) {
        const first = obj[0];
        if (first) {
            const identifier = first.identifier ?? 'id';

            const list = getList(accumulator, identifier);
            for (const item of obj) {
                list.push(item);
            }
        }
    }

    return accumulator;
};

const groupByIdentifier = (items: Array<SelectionModel>, displayTemplate: string): Map<string, SelectionModel[]> => {
    const out: Map<string, SelectionModel[]> = new Map();

    for (const s of items) {
        const tagIdentifier = s.identifier ?? s.id.toString();
        const keyGroup = displayTemplate === 'half-full-time' ? tagIdentifier.charAt(0) : tagIdentifier;
        getList(out, keyGroup).push(s);
    }

    return out;
};

const groupSelections = (items: Array<SelectionModel>, displayTemplate: string): Map<string, SelectionModel[]> => {
    if (
        displayTemplate === 'one-column' ||
        displayTemplate === 'goalscorer' ||
        displayTemplate === 'two-column' ||
        displayTemplate === 'three-column'
    ) {
        return getGroupList(items, displayTemplate);
    }

    return  groupByIdentifier(items, displayTemplate);
};

interface PropsType {
    userId: number,
    eventId: number,
    openedMarket: TypeId.MarketId,
}

@observer
class SelectionsList extends AppStateComponent<PropsType> {
    onMarketChange = (_name: unknown, _event: unknown, data: unknown): void => {
        if (isSelectedMarket(data)) {
            const marketId = parseInt(data.market, 10);

            if (!isNaN(marketId)) {
                const fullMarketId = TypeId.newMarketId(this.props.eventId, marketId);
                this.appState.marketsListState.setMarket(fullMarketId);
                return;
            }
        }

        console.error('onMarketChange - problem with decoding data', data);
    }

    renderColumnElements(selections: Array<SelectionModel>, commonPrefix: string): Array<React.ReactElement> {
        const { openedMarket} = this.props;

        const marketModel = this.appState.websocketV2.getMarket(openedMarket);
        if (marketModel === null) {
            return [];
        }

        const elements = selections
            .map(selectionModel => {
                const marketId = selectionModel.marketId;
                const selectionId = selectionModel.id;

                const priceF = selectionModel.price?.f ?? '';

                const name = selectionModel.name.slice(commonPrefix.length);

                return (
                    <li key={ selectionId }>
                        <Selection
                            userId={this.props.userId}
                            marketId={marketId}
                            selectionId={selectionId}
                        >
                            <span className='selections-list-price'>{ priceF }</span>
                            {
                                name !== '' ? (
                                    <span className='selections-list-name'> { name }</span>
                                ) : undefined
                            }
                        </Selection>
                    </li>
                );
            });
        return elements;
    }

    renderColumns(): React.ReactElement | null {
        const { openedMarket: openedMarketId } = this.props;

        const openedMarket = this.appState.websocketV2.getMarket(openedMarketId);

        if (openedMarket === null) {
            return null;
        }

        // const eventTagModel = this.appState.websocketV2.getEventTag(openedMarket.eventId);
        // if (eventTagModel === null) {
        //     return null;
        // }

        const displayOrder = openedMarket.getTag('display-order');

        // Sort selections withing a group

        const items = isCriteria(displayOrder) ?
            // Sort selections by criteria specified on display order tag at market level
            sortSelectionsByCriteria(openedMarket.selections, displayOrder) :
            openedMarket.selections;

        const displayTemplate = openedMarket.getTag('display-template') ?? '';

        const selectionsGroupsIn = groupSelections(items, displayTemplate);


        const marketTemplateId = openedMarket.templateId;
        const listNamesColumns = SELECTION_ORDER[marketTemplateId] ?? SELECTION_ORDER_default;

        const selectionsGroups = new Map([...selectionsGroupsIn.entries()].sort(([a_key, _a_list], [b_key, _b_list]): number => {
            return listNamesColumns.indexOf(a_key) > listNamesColumns.indexOf(b_key) ? -1 : 1;
        }));

        if (selectionsGroups.size > 3 || selectionsGroups.size === 1) {
            return (
                <ul className='menu'>
                    { this.renderColumnElements(items, '') }
                </ul>
            );
        } else {
            const columns: Array<React.ReactElement> = [];

            for (const [key, group] of selectionsGroups) {  //}.map((group, key) => {
                let commonPrefix = '';

                const first = group[0];

                if (first !== undefined) {
                    // eslint-disable-next-line
                    while (true) {
                        const prefix = first /*group.first()*/.name.slice(commonPrefix.length).trim().split(' ')[0];

                        if (prefix && group.every(s => s.name.startsWith(commonPrefix + prefix))) {//eslint-disable-line
                            commonPrefix += prefix + ' ';
                        } else {
                            break;
                        }
                    }
                }

                columns.push(
                    <div className='selections-list-column'>
                        { !['two-column','three-column'].includes(displayTemplate) ? (
                            <span>
                                <h4>{ commonPrefix || key }</h4>
                            </span>
                        ) : (
                            undefined
                        )}
                        <ul className='menu'>
                            { this.renderColumnElements(group, commonPrefix) }
                        </ul>
                    </div>
                );
            }

            return (
                <div className='selections-list-columns'>
                    { columns }
                </div>
            );
        }
    }

    renderEwTerms(openedMarket: MarketModel): React.ReactElement | null {
        if (openedMarket.eachWayOffered) {
            const eachWayTermsReduction = openedMarket.eachWayTermsReduction;
            const eachWayTermsPlaces = openedMarket.eachWayTermsPlaces;

            return (
                <ul className='ewTerms'>
                    <li>EW Terms</li>
                    <li>{ eachWayTermsReduction }</li>
                    <li>{ eachWayTermsPlaces } Places</li>
                </ul>
            );
        }

        return null;
    }

    render(): React.ReactElement | null {
        const { eventId, openedMarket: openedMarketId } = this.props;

        const openedMarket = this.appState.websocketV2.getMarket(openedMarketId);
        const eventModel = this.appState.websocketV2.getEvent(eventId);

        if (openedMarket === null || eventModel === null) {
            return null;
        }

        const marketGroups = getMarketList(eventModel, () => true);

        return (
            <div className='selections-list'>
                <a href='#' className='back' onClick={ this.appState.marketsListState.closeMarket }>&laquo; Back</a>
                { this.renderEwTerms(openedMarket) }
                <ChangeMarketForm
                    markets={ marketGroups }
                    data={{ market: openedMarketId }}
                    onChange={ this.onMarketChange }
                />
                { this.renderColumns() }
            </div>
        );
    }
}

export default SelectionsList;
